import { IDetailsRowProps } from '@fluentui/react';
import {
  CommonStyles,
  debounce,
  DetailsList,
  getItemsWithEmptyItemsOfNextPage,
  IColumnWithDataType,
  IconButton,
  IconName,
  IDetailsListProps,
  listCellStylesNoLeftRightPadding,
  ListRow,
  listRowStylesWidgetLine,
  useTheme,
  useToast,
  WidgetPanel,
  widgetPanelStylesNoBGWhiteBody,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  PinnedFeatureSet,
  PinnedFeatureSetsRequest,
  PinnedFeatureSetsResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { defaultPageSize } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';

interface FeatureSetListData {
  items: PinnedFeatureSet[];
  pageToken?: string;
  pageSize?: number;
}

const getColumns = (onUnpin: (featureSetId?: string) => void) => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'featureSetName',
      name: 'Feature Set Name',
      fieldName: 'featureSetDescription',
      minWidth: 100,
      data: {
        headerFieldName: 'featureSetName',
        listCellProps: { emptyMessage: 'No Description', styles: listCellStylesNoLeftRightPadding },
      },
    },
    {
      key: 'pinnedAt',
      name: 'Pinned On',
      fieldName: 'pinnedAt',
      minWidth: 130,
      data: {
        listCellProps: {
          onRenderText: (item: PinnedFeatureSet) => <>{formatDefaultDateAndTimeWithSeconds(item.pinnedAt) || `-`}</>,
          styles: listCellStylesNoLeftRightPadding,
        },
      },
    },
    {
      key: 'updatedAt',
      name: 'Updated On',
      fieldName: 'updatedAt',
      minWidth: 130,
      data: {
        listCellProps: {
          onRenderText: (item: PinnedFeatureSet) => <>{formatDefaultDateAndTimeWithSeconds(item.updatedAt) || `-`}</>,
          styles: listCellStylesNoLeftRightPadding,
        },
      },
    },
    {
      key: 'unpin',
      name: 'Unpin',
      minWidth: 28,
      data: {
        listCellProps: { styles: listCellStylesNoLeftRightPadding },
      },
      onRender: (item: PinnedFeatureSet) => {
        const { featureSetId } = item;
        return (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              width: '100%',
              textAlign: 'right',
            }}
          >
            <div>
              <IconButton
                title="Unpin"
                iconName={IconName.PinnedSolid}
                onClick={(e) => {
                  onUnpin(featureSetId);
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            </div>
          </div>
        );
      },
    },
  ];
  return columns;
};

export const PinnedFeatureSetListWidget = () => {
  const [data, setData] = useState<FeatureSetListData>({ items: [] });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const refMeta = useRef<{
    isLoadingPage: boolean;
  }>({
    isLoadingPage: false,
  });
  const history = useHistory();
  const theme = useTheme();
  const { addToast } = useToast();
  const load = useCallback(
    async ({ items, pageToken }: FeatureSetListData = { items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading pinned feature sets...`);
      const pageSize = defaultPageSize;
      try {
        const req: PinnedFeatureSetsRequest = {
          pageSize,
          pageToken,
        };
        const { featureSets = [], nextPageToken = '' }: PinnedFeatureSetsResponse = await fsApi(
          CoreService.ListPinnedFeatureSets,
          req
        );
        const newItems = getItemsWithEmptyItemsOfNextPage(items, featureSets, pageSize);
        const newData = {
          items: newItems,
          pageToken: nextPageToken,
          pageSize,
        };
        setData({
          ...newData,
        });
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading pinned feature sets: `));
      } finally {
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
      }
    },
    [addToast]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const onUnpin = useCallback(
    async (featureSetId?: string) => {
      try {
        await fsApi(CoreService.UnpinFeatureSet, {
          featureSetId,
        });
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while unpinning the feature set: `));
      } finally {
        load();
      }
    },
    [addToast]
  );
  const detailsListProps: IDetailsListProps = {
    columns: getColumns(onUnpin),
    isHeaderVisible: false,
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      if (item.isNotLoaded && !refMeta.current.isLoadingPage) {
        refMeta.current.isLoadingPage = true;
        onLoad(data, false);
      }
      return (
        <ListRow
          styles={listRowStylesWidgetLine}
          data={item}
          columns={columns}
          loading={item.isNotLoaded}
          onClick={(e) => {
            history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${item.featureSetId}`);
            e?.preventDefault();
          }}
        />
      );
    },
  };
  useEffect(() => {
    load();
  }, []);
  return (
    <WidgetPanel
      data-test="pinned-feature-set-list-widget"
      styles={widgetPanelStylesNoBGWhiteBody}
      title="Pinned Feature sets"
      loadingMessage={loadingMessage}
      emptyMessageProps={
        !loadingMessage && data.items.length === 0
          ? {
              message: `You don't have any pinned feature sets.`,
            }
          : undefined
      }
    >
      <div
        style={{ height: '100%', overflow: 'auto', backgroundColor: theme.semanticColors?.contentBackground }}
        data-is-scrollable="true"
      >
        <DetailsList {...detailsListProps} />
      </div>
    </WidgetPanel>
  );
};
